import React, { memo, useState } from 'react';
import createClasses from './styles';
import clsx from 'clsx';
import { Button, CustomIcon, Tooltip } from '@p81-common/ui';
import { useRegion } from 'contexts/RegionContext';
import { env } from 'env';

interface Region {
  code: string;
  fullName: string;
  flagName: string;
}

const RegionSwitcher = () => {
  const classes = createClasses();
  const { region, setRegion, availableRegions } = useRegion();
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});

  const onRegionChange = (newRegion: Region) => {
    setIsLoading(prev => ({ ...prev, [newRegion.code]: true }));
    setTimeout(() => {
      setRegion(newRegion.code);
      setIsLoading(prev => ({ ...prev, [newRegion.code]: false }));
    }, 300);
  };

  // Guard for rendering outside RegionProvider - the default context value's
  // empty array. The regions themselves are a static module-level constant, so
  // there is no async load. Returning null rather than a differently-sized
  // placeholder keeps the bar from reflowing in that case.
  if (!availableRegions || availableRegions.length === 0) {
    return null;
  }

  return (
    <div className={classes.root} role="group" aria-label="Region">
      {availableRegions?.map(availableRegion => (
        <Tooltip title={availableRegion.fullName} key={availableRegion.code}>
          <Button
            clickArea={false}
            // The active button is disabled, so its Tooltip never fires and the
            // code is hidden below customsm - these carry the label instead.
            aria-label={availableRegion.fullName}
            aria-current={
              availableRegion.code === region.code ? 'true' : undefined
            }
            className={clsx(
              availableRegion.code === region.code && classes.activeButton,
              classes.button,
              isLoading[availableRegion.code] && classes.loadingButton
            )}
            onClick={() => onRegionChange(availableRegion)}
            size="small"
            // Disable the button if it's the currently selected region or if it's in loading state
            disabled={
              isLoading[availableRegion.code] ||
              availableRegion.code === region.code
            }
          >
            <CustomIcon
              src={`${env.REACT_APP_STATIC_ASSETS_URL}/jarvis/Jarvis flags/${availableRegion.flagName}.svg`}
              color=""
              className={classes.icon}
            />
            <span className={classes.code}>{availableRegion.code}</span>
          </Button>
        </Tooltip>
      ))}
    </div>
  );
};

const m = memo(RegionSwitcher);
export { m as RegionSwitcher };
